import { config } from '@/config'
import { MetamaskSubprovider, SignerSubprovider, RPCSubprovider, Web3ProviderEngine } from '@0x/subproviders'
import { BigNumber } from '@0x/utils'
import { Web3Wrapper } from '@0x/web3-wrapper'
import { ContractWrappers, ERC20TokenContract } from '@0x/contract-wrappers'
// import { getContractAddressesForChainOrThrow } from '@0x/contract-addresses'
import { providerUtils } from '@0x/utils'
import { findTokenByAddress, parseOrder, getCurrentGasPrice } from '@/utils/utils'
import { searchTokenByAddress } from '@/utils/TokenUtils'
import { AssetProxyId } from '@0x/types'
import { HttpClient } from '@0x/connect'
import { assetDataUtils } from '@0x/order-utils'
import Vue from 'vue'

// --------------------------------------------------------
function _getCurrentTime() {
	return (new Date()).getTime()
}

// --------------------------------------------------------
async function _loadRelayerAssetPairs (relayerApiURL) {
	const httpClient = new HttpClient(relayerApiURL)
	try {
		const relayerAssetPairInfo = await httpClient.getAssetPairsAsync(
			{perPage: 1000}
		)
		// console.log(relayerApiURL)
		// console.log(relayerAssetPairInfo)
		if (relayerAssetPairInfo && relayerAssetPairInfo.records) {
			return relayerAssetPairInfo.records
		}
		else {
			return null
		}
	} catch (err) {
		console.log(err)
		return null
	}
}

export default {

	// ---------------------------------------------------------------------
	// loadNetwork
	//----------------------------------------------------------------------
	async loadNetwork ({commit, state, dispatch}, noCache=false) {

		// console.log('Action: loadNetwork')

		const now = _getCurrentTime()

		if (config.backendURL.includes('localhost')) {
			console.log('backend is localhost')
		}

		// Check if we can skip
		if (!noCache && state.network.lastLoadTime && now - state.network.lastLoadTime <= config.cacheTime.network) {
			// console.log('no need to reload network')
			return
		}

		// Make sure wallet is connected
		if (!window.ethereum || !window.ethereum.selectedAddress || !window.ethereum.networkVersion) {
			throw new Error ("Ethereum wallet not connected")
		}

		console.log("isMetaMask: " + window.ethereum.isMetaMask)

		const networkId = parseInt(window.ethereum.networkVersion)
		const rpcUrl = config.network[networkId].rpc
		const networkName = config.network[networkId].name

		const providerEngine = new Web3ProviderEngine()
		const signer = window.ethereum.isMetaMask
			? new MetamaskSubprovider(window.web3.currentProvider)
			: new SignerSubprovider(window.web3.currentProvider)
		providerEngine.addProvider(signer)
		providerEngine.addProvider(new RPCSubprovider(rpcUrl))
		providerUtils.startProviderEngine(providerEngine);

		const web3Wrapper = new Web3Wrapper(providerEngine)
		const accounts = await web3Wrapper.getAvailableAddressesAsync()
		if (!accounts || !accounts.length) {
			throw new Error ("Error in getting account")
		}
		const myAccount = accounts[0].toLowerCase()

		const contractWrappers = new ContractWrappers(providerEngine, { chainId: networkId })
		const contractAddresses = contractWrappers.contractAddresses

		const network = {
			lastLoadTime: now,
			id: networkId,
			name: networkName,
			userAccount: myAccount,
			web3Wrapper: web3Wrapper,
			providerEngine: providerEngine,
			contractWrappers: contractWrappers,
			contractAddresses: contractAddresses,
		}

		// getCurrentGasPrice(networkId)  // No need to wait

		commit('setNetwork', network)
		// console.log('Action: Done loadNetwork')
		return
	},

	// ---------------------------------------------------------------------
	// loadTokenInfo
	//----------------------------------------------------------------------
	async loadTokenInfo ({commit, state, dispatch}, noCache=false, preloadedRes=null) {

		// console.log('Action: loadTokenInfo')

		const now = _getCurrentTime()

		// Check if we can skip
		if (!noCache && state.tokenInfo.lastLoadTime && now - state.tokenInfo.lastLoadTime <= config.cacheTime.tokenInfo) {
			// console.log('no need to reload tokenInfo')
			return
		}

		// Make sure network is loaded
		if (!state.network.lastLoadTime) {
			await dispatch('loadNetwork')
		}

		const tokenInfo = {
			verifiedTokenList: [],
			topTokenList: [],
			tradingPairBaseList: [],
			popularTradingPairList: [],
			ERC20TokenUpdates: {},
			ZRXToken: { ...config.ZRXTokenDefault },
			WETHToken: { ...config.WETHTokenDefault }
		}

		// Get ZRX Token
		if (!state.network.contractAddresses.zrxToken) {
			throw new Error('Cannot get zrxToken address')
		}
		tokenInfo.ZRXToken.address = state.network.contractAddresses.zrxToken.toLowerCase()

		// Get WETH Token
		if (!state.network.contractAddresses.etherToken) {
			throw new Error('Cannot get etherToken address')
		}
		tokenInfo.WETHToken.address = state.network.contractAddresses.etherToken.toLowerCase()

		let res = preloadedRes
		if (!res || !res.body.tokenList) {
			try {
				const url = `${config.backendAPIURL}/${state.network.id}/erc20-token-defaults`
				res = await Vue.http.get(url)
				// console.log(res)
			} catch (err) {
				console.log(err)
				throw new Error ("Cannot get ERC20 token defaults from backend")
			}
		}

		const tokenList = res.body.tokenList
		const topTokenList = res.body.topTokenList
		const ERC20TokenUpdates = res.body.ERC20TokenUpdates
		// console.log(ERC20TokenUpdates)
		const popularTradingPairList = res.body.tradingPairList
		const tradingPairBaseList = res.body.tradingPairBaseList
		for (let token of tokenList) {
			token.address = token.address.toLowerCase()
			tokenInfo.verifiedTokenList.push(token)
		}
		tokenInfo.topTokenList = topTokenList
		tokenInfo.ERC20TokenUpdates = ERC20TokenUpdates
		tokenInfo.popularTradingPairList = popularTradingPairList
		tokenInfo.tradingPairBaseList = tradingPairBaseList

		tokenInfo.lastLoadTime = now

		commit('setTokenInfo', tokenInfo)

	},

	// ---------------------------------------------------------------------
	// loadAccount
	//----------------------------------------------------------------------
	async loadAccount ({commit, state, dispatch}, noCache=false) {

		// console.log('Action: loadAccount')

		const now = _getCurrentTime()

		// Check if we can skip
		if (!noCache && state.account.lastLoadTime && now - state.account.lastLoadTime <= config.cacheTime.account) {
			// console.log('no need to reload account')
			return
		}

		if (!state.network.lastLoadTime) {
			await dispatch('loadNetwork')
		}
		if (!state.tokenInfo.lastLoadTime) {
			await dispatch('loadTokenInfo')
		}

		if (!state.network.userAccount) {
			throw new Error ("Cannot get userAccount")
		}
		const myAddress = state.network.userAccount

		const account = {
			ETHBalance: null,
			WETHBalance: null,
			ZRXBalance: null,
			balanceByAddress: {},
			allowanceByAddress: {},
			trackedTokens: [],
			trackedTokenPairs: []
		}

		// Get tracked token from backend
		let backendTokenTrackingExist = false
		try {
			const url = `${config.backendAPIURL}/${state.network.id}/token-tracking/${myAddress}`
			const res = await Vue.http.get(url)
			if (res.body && res.body.tokens) {
				// console.log('trackedTokens available on backend')
				account.trackedTokens = res.body.tokens
				backendTokenTrackingExist = true
				// console.log('got user tracking info', trackedTokens)
			}
		} catch (err) {
			console.log(err)
			throw new Error ("Cannot get tracked ERC20 token list from backend")
		}

		// For new user, construct a list of default tracked tokens
		if (!backendTokenTrackingExist) {
			const defaultTrackedTokens = []
			for (let token of state.tokenInfo.verifiedTokenList) {
				if (config.defaultTokenList.includes(token.symbol)) {
					defaultTrackedTokens.push(token)
				}
			}
			const data = {
				tokens: defaultTrackedTokens
			}
			try {
				const url = `${config.backendAPIURL}/${state.network.id}/token-tracking/${myAddress}`
				const res = await Vue.http.post(url, data)
				if (res.body.tokens) {
					// console.log('created tracked tokens done')
					account.trackedTokens = res.body.tokens;
				}
			} catch (err) {
				console.log(err)
				throw new Error ("Cannot create new token tracking from backend")
			}
		}

		// Get tracked token pairs from backend
		let backendTokenPairTrackingExist = false
		try {
			const url = `${config.backendAPIURL}/${state.network.id}/token-pair-tracking/${myAddress}`
			const res = await Vue.http.get(url)
			if (res.body && res.body.tokenPairs) {
				// console.log('trackedTokenPairs available on backend')
				account.trackedTokenPairs = res.body.tokenPairs
				backendTokenPairTrackingExist = true
			}
		} catch (err) {
			console.log(err)
			throw new Error ("Cannot get tracked token pairs from backend")
		}

		if (!backendTokenPairTrackingExist) {
			account.trackedTokenPairs = []
		}

		// Add WETH if not in tracked token list
		let WETHTokenFound = false
		for (let tokenInfo of account.trackedTokens) {
			if (tokenInfo.address === state.tokenInfo.WETHToken.address) {
				WETHTokenFound = true
				break
			}
		}
		if (WETHTokenFound === false) {
			account.trackedTokens.push(state.tokenInfo.WETHToken)
		}

		// Make sure tracked token list has no duplicates
		const seen = {}
		const cleanList = []
		for (let token of account.trackedTokens) {
			const address = token.address
			if (!seen[address]) {
				cleanList.push(token)
				seen[address] = true
			}
		}
		account.trackedTokens = [ ...cleanList ]

		for (let token of state.tokenInfo.tradingPairBaseList) {
			const address = token.address
			if (!seen[address]) {
				cleanList.push(token)
				seen[address] = true
			}
		}

		const addressList = [ ...cleanList ]


		// get balance and allowance for tracked tokens
		let balanceList = null
		let allowanceList = null
		const assetDataList = []
		for (let token of addressList) {
			const address = token.address
			const assetData = await state.network.contractWrappers.devUtils.encodeERC20AssetData(address).callAsync()
			assetDataList.push(assetData)
		}
		try {
			const balanceAndAllowance = await state.network.contractWrappers.devUtils.getBatchBalancesAndAssetProxyAllowances(myAddress, assetDataList).callAsync()
			balanceList = balanceAndAllowance[0]
			allowanceList = balanceAndAllowance[1]
			// console.log(balanceAndAllowance)
		} catch (err) {
			console.log(err)
			throw new Error ("Cannot get balance and allowance for tokens")
		}

		for (var i = 0; i < addressList.length; i++) {
			const decimals = addressList[i].decimals
			const address = addressList[i].address

			const balance = balanceList[i]
			const numBalance = Web3Wrapper.toUnitAmount(balance, decimals).toNumber()
			account.balanceByAddress[address] = numBalance
			if (address === state.tokenInfo.WETHToken.address) {
				account.WETHBalance = numBalance
			}
			if (address === state.tokenInfo.ZRXToken.address) {
				account.ZRXBalance = numBalance
			}

			const allowance = allowanceList[i]
			const numAllowance = Web3Wrapper.toUnitAmount(allowance, decimals).toNumber()
			account.allowanceByAddress[address] = numAllowance
		}

		// Get ETH Balance
		const ETHBalance = await state.network.web3Wrapper.getBalanceInWeiAsync(myAddress)
		const numETHBalance = Web3Wrapper.toUnitAmount(ETHBalance, config.ETHDecimals).toNumber()
		account.ETHBalance = numETHBalance

		account.lastLoadTime = now
		commit('setAccount', account)

	},


	// --------------------------------------------------------
	// loadRelayerInfo
	// --------------------------------------------------------
	async loadRelayerInfo ({commit, state, dispatch}, noCache=false) {

		const now = _getCurrentTime()

		// Check if we can skip
		if (!noCache && state.relayerInfo.lastLoadTime && now - state.relayerInfo.lastLoadTime <= config.cacheTime.relayerInfo) {
			// console.log('no need to reload relayer info')
			return
		}

		const relayerInfo = {
			lastLoadTime: null,
			ERC20AssetPairs: {}
		}

		try {
			for (let relayer of state.config.network[state.network.id].relayers) {
				const name = relayer.name
				const url = relayer.apiURL
				// Ignore LedgerDex's Token Pair Checking
				// if (name === 'LedgerDex') {
				// 	continue
				// }
				// console.log('loading asset pairs for ' + name)
				const relayerAssetPairs = await _loadRelayerAssetPairs(url)
				// console.log(relayerAssetPairs)
				if (relayerAssetPairs && relayerAssetPairs.length > 0) {
					const pairsInfo = {}
					for (let pairs of relayerAssetPairs) {
						const assetDataA = pairs.assetDataA.assetData
						const assetDataADecoded = assetDataUtils.decodeAssetDataOrThrow(assetDataA)
						if (assetDataADecoded.assetProxyId !== AssetProxyId.ERC20) {
							// console.log('Not ERC20: ' + assetDataA)
							continue
						}
						const assetDataB = pairs.assetDataB.assetData
						const assetDataBDecoded = assetDataUtils.decodeAssetDataOrThrow(assetDataB)
						if (assetDataBDecoded.assetProxyId !== AssetProxyId.ERC20) {
							// console.log('Not ERC20: ' + assetDataB)
							continue
						}
						const key1 = assetDataA + '-' + assetDataB
						const key2 = assetDataB + '-' + assetDataA
						pairsInfo[key1] = true
						pairsInfo[key2] = true
					}
					relayerInfo.ERC20AssetPairs[name] = pairsInfo
				}
			}
		} catch (err) {
		 	console.log(err)
		 	throw err
		}

		// console.log(relayerInfo)
		relayerInfo.lastLoadTime = _getCurrentTime()
		commit('setRelayerInfo', relayerInfo)
		return
		// console.log('Action: Done loadNetwork')
	},

	// ---------------------------------------------------------------------
	// clearAllState
	// ---------------------------------------------------------------------
	clearAllState ({commit, state, dispatch}) {

		commit('setNetwork', {})
		commit('setTokenInfo', {})
		commit('setAccount', {})
		commit('setRelayerInfo', {})

	},

	// ---------------------------------------------------------------------
	// parseSignedERC20Order  ({commit, state, dispatch}, tokenAssetData) {
	// ---------------------------------------------------------------------
	async parseSignedOrder  ({commit, state, dispatch}, order) {

    const makerAsset = await dispatch('getTokenInfoByAssetData', order.makerAssetData)
    const takerAsset = await dispatch('getTokenInfoByAssetData', order.takerAssetData)

    const metaData = {
      makerAsset,
      takerAsset,
    }

    const makerFee = new BigNumber(order.makerFee)
    if (makerFee > 0 && order.makerFeeAssetData.length > 2) {
      const makerFeeAsset = await dispatch('getTokenInfoByAssetData', order.makerFeeAssetData)
      metaData.makerFeeAsset = makerFeeAsset
    }

    const takerFee = new BigNumber(order.takerFee)
    if (takerFee > 0 && order.takerFeeAssetData.length > 2) {
      const takerFeeAsset = await dispatch('getTokenInfoByAssetData', order.takerFeeAssetData)
      metaData.takerFeeAsset = takerFeeAsset
    }

    // console.log(metaData)

    try {
    	const parsedOrder = await parseOrder(order, metaData)
    	return parsedOrder
    } catch (err) {
    	throw err
    }

	},

	// ---------------------------------------------------------------------
	// getTokenInfoByAssetData
	//----------------------------------------------------------------------
	async getTokenInfoByAssetData  ({commit, state, dispatch}, tokenAssetData) {

		const assetDataDecoded = assetDataUtils.decodeAssetDataOrThrow(tokenAssetData)
		if (assetDataDecoded.assetProxyId !== AssetProxyId.ERC20) {
			throw new Error ('Not an ERC20 token: ' + tokenAssetData)
		}
		const tokenAddress = assetDataDecoded.tokenAddress
		const result = await dispatch('getTokenInfoByAddress', tokenAddress)
		return result
	},

	// ---------------------------------------------------------------------
	// getTokenInfoByAddress
	//----------------------------------------------------------------------
	async getTokenInfoByAddress ({commit, state, dispatch}, tokenAddress) {

		if (!state.network.lastLoadTime) {
			await dispatch('loadNetwork')
		}
		if (!state.tokenInfo.lastLoadTime) {
			await dispatch('loadTokenInfo')
		}
		if (!state.account.lastLoadTime) {
			await dispatch('loadAccount')
		}

		tokenAddress = tokenAddress.toLowerCase()


		const localStorageKey = 'tokeninfo-' + state.network.id + '-' + tokenAddress

		// check TokenUpdates
		if(state.tokenInfo.ERC20TokenUpdates && state.tokenInfo.ERC20TokenUpdates[tokenAddress]) {
			// console.log('Found via ERC20TokenUpdates')
			const tokenInfo = state.tokenInfo.ERC20TokenUpdates[tokenAddress]
			localStorage.setItem(localStorageKey, JSON.stringify(tokenInfo))
			return tokenInfo
		}

		// check local storage
		const localStored = localStorage.getItem(localStorageKey)
		if (localStored) {
			const tokenInfo = JSON.parse(localStored)
			// console.log('got tokenInfo from local storage: ', tokenInfo)
			return tokenInfo
		}

		// Check Tracked Tokens
		const trackedToken = findTokenByAddress(state.account.trackedTokens, tokenAddress)
		if (trackedToken) {
			localStorage.setItem(localStorageKey, JSON.stringify(trackedToken))
			// console.log('got tokenInfo from tracked token')
			return trackedToken
		}

		// Check Verified Tokens
		const verifiedToken = findTokenByAddress(state.tokenInfo.verifiedTokenList, tokenAddress)
		if (verifiedToken) {
			localStorage.setItem(localStorageKey, JSON.stringify(verifiedToken))
			// console.log('got tokenInfo from verifiedToken')
			return verifiedToken
		}

		// Check Top Tokens
		const topToken = findTokenByAddress(state.tokenInfo.topTokenList, tokenAddress)
		if (topToken) {
			localStorage.setItem(localStorageKey, JSON.stringify(topToken))
			// console.log('got tokenInfo from topToken')
			return topToken
		}

		// Try gettting allowance
		const myAddress = state.network.userAccount
		if (!myAddress) {
			throw new Error ("Cannot get userAccount")
		}
		try {
			const erc20ProxyAddress = state.network.contractWrappers.contractAddresses.erc20Proxy
			const token = new ERC20TokenContract(tokenAddress, state.network.contractWrappers.getProvider())
			await token.allowance(myAddress, erc20ProxyAddress).callAsync()
			console.log('Passed allowance checking')
			// console.log(allowanceTest)
		} catch (err) {
			console.log('Failed allowance checking: ', err)
			return null
		}

		// Load token info from the blockchain with the standard ABI
		// console.log('trying standardABISearch')
		const tokenStandardABISearch = await searchTokenByAddress(tokenAddress)
		if (tokenStandardABISearch) {
			localStorage.setItem(localStorageKey, JSON.stringify(tokenStandardABISearch))
			// console.log('got tokenInfo from standardABISearch')
			return tokenStandardABISearch
		}

		// Load token from the blockchain with their own ABI
		const etherscanApiURL = state.config.network[state.network.id].etherscanApiURL
			+ 'api?module=contract&action=getabi&address=' + tokenAddress + ''
		// console.log('try etherscanApiURL', etherscanApiURL)
		const etherscanRes = await Vue.http.get(etherscanApiURL)
		// console.log(etherscanRes)
		if (etherscanRes && etherscanRes.body && etherscanRes.body.result) {
			let tokenABI = null
			try {
				tokenABI = JSON.parse(etherscanRes.body.result)
			} catch (err) {
				console.log(err)
			}
			if (tokenABI) {
				const tokenABISearch = await searchTokenByAddress(tokenAddress, tokenABI)
				if (tokenABISearch) {
					localStorage.setItem(localStorageKey, JSON.stringify(tokenABISearch))
					return tokenABISearch
				}
			}
		}

		return null

	},

	// --------------------------------------------------------
	async updateAccountForToken ({commit, state, dispatch}, tokenInfo) {

		// console.log('updateAccountForToken')

		if (!state.network.lastLoadTime) {
			await dispatch('loadNetwork')
		}
		if (!state.tokenInfo.lastLoadTime) {
			await dispatch('loadTokenInfo')
		}
		if (!state.account.lastLoadTime) {
			await dispatch('loadAccount')
		}

		const myAddress = state.network.userAccount
		if (!myAddress) {
			throw new Error("cannot get my address")
			// return
		}

		if (!tokenInfo.address || !tokenInfo.symbol ||
			tokenInfo.decimals === undefined || tokenInfo.decimals === null ) {
			throw new Error('missing tokenInfo info')
			// return
		}

		const decimals = tokenInfo.decimals
		const address = tokenInfo.address.toLowerCase()
		const symbol = tokenInfo.symbol

		const account = { ...state.account }

		try {

			const assetData = await state.network.contractWrappers.devUtils.encodeERC20AssetData(address).callAsync()
			const assetDataList = [ assetData ]
			const balanceAndAllowance = await state.network.contractWrappers.devUtils.getBatchBalancesAndAssetProxyAllowances(myAddress, assetDataList).callAsync()
			const balanceList = balanceAndAllowance[0]
			const allowanceList = balanceAndAllowance[1]

			const balance = balanceList[0]
			const numBalance = Web3Wrapper.toUnitAmount(balance, decimals).toNumber()
			account.balanceByAddress[address] = numBalance

			const allowance = allowanceList[0]
			const numAllowance = Web3Wrapper.toUnitAmount(allowance, decimals).toNumber()
			account.allowanceByAddress[address] = numAllowance

			account.lastLoadTime = _getCurrentTime()
			commit('setAccount', account)

		} catch (err) {
			throw err
		}

	},

}
